/**
 * Atlas PM · 项目星图 · 背景（星尘 + 参考网格）
 *
 * 对照原型 makeBackgroundStars / makeGrid。
 * 所有对象直接挂到 scene，统一由 scene.traverse 释放。
 */

import * as THREE from 'three';

const BG_COLOR = 0x02050b;

/** 背景星尘：1400 个随机星点，青 / 紫 / 白三色混合，加色混合 */
export function addBackgroundStars(scene: THREE.Scene): THREE.Points {
  const N = 1400;
  const pos = new Float32Array(N * 3);
  const col = new Float32Array(N * 3);

  for (let i = 0; i < N; i++) {
    const r = 400 + Math.random() * 700;
    const th = Math.random() * Math.PI * 2;
    const ph = Math.acos(2 * Math.random() - 1);
    pos[i * 3] = r * Math.sin(ph) * Math.cos(th);
    pos[i * 3 + 1] = r * Math.cos(ph) * 0.7;
    pos[i * 3 + 2] = r * Math.sin(ph) * Math.sin(th);

    const t = Math.random();
    const c = t > 0.86 ? [0.66, 0.48, 1.0] : t > 0.6 ? [0.37, 0.9, 1.0] : [0.72, 0.82, 0.95];
    col[i * 3] = c[0] ?? 0.8;
    col[i * 3 + 1] = c[1] ?? 0.8;
    col[i * 3 + 2] = c[2] ?? 0.8;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(col, 3));

  const material = new THREE.PointsMaterial({
    size: 1.7,
    vertexColors: true,
    transparent: true,
    opacity: 0.62,
    sizeAttenuation: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geometry, material);
  scene.add(points);
  return points;
}

/** 极坐标参考网格（同心圆 + 放射线），置于 y=-12 平面 */
export function addGrid(scene: THREE.Scene): THREE.Group {
  const group = new THREE.Group();
  const material = new THREE.LineBasicMaterial({
    color: 0x1a3a5c,
    transparent: true,
    opacity: 0.34,
    depthWrite: false,
  });

  for (const r of [18, 32, 46, 60]) {
    const pts: THREE.Vector3[] = [];
    for (let i = 0; i <= 96; i++) {
      const a = (i / 96) * Math.PI * 2;
      pts.push(new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r));
    }
    group.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(pts), material));
  }

  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    group.add(
      new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(Math.cos(a) * 60, 0, Math.sin(a) * 60),
        ]),
        material,
      ),
    );
  }

  group.position.y = -12;
  scene.add(group);
  return group;
}

/** 场景底色 + 雾效（与原型一致） */
export function setupSceneFog(scene: THREE.Scene): void {
  scene.background = new THREE.Color(BG_COLOR);
  scene.fog = new THREE.FogExp2(BG_COLOR, 0.0042);
}

/** 光照：环境光 + 青 / 紫两个点光 */
export function addStarLights(scene: THREE.Scene): void {
  scene.add(new THREE.AmbientLight(0x8899bb, 1.5));
  const p1 = new THREE.PointLight(0x5ee7ff, 2.4, 400);
  p1.position.set(60, 60, 80);
  const p2 = new THREE.PointLight(0xa77bff, 1.8, 400);
  p2.position.set(-70, -40, 60);
  scene.add(p1, p2);
}
